<script setup lang="ts">
import Popover from 'primevue/popover';
import HelpAIChat from '@/components/ai/HelpAIChat.vue';
import { useAIAssistantContext } from '@/composables/useAIAssistantContext';
import { useKnowledgeSourceStore } from '@/store/knowledgeSourceStore';
import { useAlerts } from '@/utils/helpers/alerts';
import { resolveTranslation } from '@/utils/helpers/i18n-content';
import type { Point2D } from '@/utils';

const POSITION_KEY = 'helpWidgetPosition';
const MODE_KEY = 'helpWidgetMode';

const knowledgeSourceStore = useKnowledgeSourceStore();
const message = useAlerts();
const popover = ref();
const helpCircleEl = ref<HTMLElement>();
const isDragging = ref(false);
const wasDragged = ref(false);
const dragStart = ref<Point2D>({ x: 0, y: 0 });
const pointerOrigin = ref<Point2D>({ x: 0, y: 0 });
const DRAG_THRESHOLD = 6;
const hasUnreadChat = ref(false);
const isCrispChatOpen = ref(false);
const showAiChat = ref(false);

// Mode: 'float' (draggable) or 'header' (inline in TopHeader)
const mode = ref<'float' | 'header'>(
  (localStorage.getItem(MODE_KEY) as 'float' | 'header') || 'float',
);
const isFloating = computed(() => mode.value === 'float');

function getDefaultPosition(): Point2D {
  return {
    x: window.innerWidth - 72,
    y: window.innerHeight - 72,
  };
}

function loadPosition(): Point2D {
  try {
    const stored = localStorage.getItem(POSITION_KEY);
    if (stored) {
      return clampPosition(JSON.parse(stored) as Point2D);
    }
  } catch {
    // ignore
  }

  return getDefaultPosition();
}

function savePosition(pos: Point2D) {
  localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
}

function clampPosition(pos: Point2D): Point2D {
  const size = 48;

  return {
    x: Math.max(0, Math.min(window.innerWidth - size, pos.x)),
    y: Math.max(0, Math.min(window.innerHeight - size, pos.y)),
  };
}

const position = ref<Point2D>(loadPosition());

const windowWidth = ref(window.innerWidth);

// The AI assistant's panel docks at the right edge of the window (WI #5769):
// while it is open the widget shows left of it, over the page instead of the
// panel's input, and goes back to its place when the panel closes.
const AI_DOCK_GAP = 24;
const { isDockOpen: isAIDockOpen, dockWidth: aiDockWidth } =
  useAIAssistantContext();

function besideAIDock(pos: Point2D): Point2D {
  if (!isAIDockOpen.value) {
    return pos;
  }
  const maxX = windowWidth.value - aiDockWidth.value - 48 - AI_DOCK_GAP;

  return { x: Math.max(0, Math.min(pos.x, maxX)), y: pos.y };
}

const shownPosition = computed<Point2D>(() =>
  !isFloating.value || (isDragging.value && wasDragged.value)
    ? position.value
    : besideAIDock(position.value),
);

// Expand direction — locked during drag to prevent mid-drag flipping
const lockedExpandsLeft = ref<boolean | null>(null);
const expandsLeft = computed(() => {
  if (isDragging.value && lockedExpandsLeft.value !== null) {
    return lockedExpandsLeft.value;
  }

  return shownPosition.value.x > window.innerWidth / 2;
});

/**
 * Dynamically cap the help-panel height so it never overflows the viewport.
 *
 * PrimeVue's Popover opens upward when the trigger is in the lower half of the
 * screen, and downward when in the upper half.  We measure both available
 * spaces and pick the larger one (which is what the Popover will choose), then
 * subtract a small margin so the panel never touches the viewport edge.
 */
const WIDGET_SIZE = 48;
// Conservative margins: PrimeVue adds ~14px gap + extra safety buffer.
// Keep generous so the panel bottom never clips on any screen size.
const POPOVER_OFFSET = 20;
const SAFETY_MARGIN = 16;
const MAX_PANEL_HEIGHT = 560;

// Reactive viewport height — window.innerHeight is NOT reactive on its own,
// so reading it directly inside a computed gives a stale value after resizes.
const windowHeight = ref(window.innerHeight);

const helpPanelStyle = computed(() => {
  let available: number;

  if (isFloating.value) {
    const spaceAbove = shownPosition.value.y - POPOVER_OFFSET - SAFETY_MARGIN;
    const spaceBelow =
      windowHeight.value -
      shownPosition.value.y -
      WIDGET_SIZE -
      POPOVER_OFFSET -
      SAFETY_MARGIN;
    available = Math.max(spaceAbove, spaceBelow);
  } else {
    // Header mode: popover opens downward below the top bar (~56px)
    available = windowHeight.value - 56 - POPOVER_OFFSET - SAFETY_MARGIN;
  }

  const h = Math.min(Math.max(available, 200), MAX_PANEL_HEIGHT);

  return { maxHeight: `${h}px` };
});

const CHAT_WIDTH = 340;
const CHAT_HEIGHT = 500;
const CHAT_GAP = 12;
const VIEWPORT_MARGIN = 8;
const HEADER_HEIGHT = 56;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

const chatOverlayStyle = computed(() => {
  const width = Math.min(CHAT_WIDTH, windowWidth.value - 2 * VIEWPORT_MARGIN);
  const height = Math.min(
    CHAT_HEIGHT,
    windowHeight.value - 2 * VIEWPORT_MARGIN,
  );
  const maxLeft = windowWidth.value - width - VIEWPORT_MARGIN;
  const maxTop = windowHeight.value - height - VIEWPORT_MARGIN;

  let left: number;
  let top: number;
  if (isFloating.value) {
    left = shownPosition.value.x - width - CHAT_GAP;
    if (left < VIEWPORT_MARGIN) {
      left = shownPosition.value.x + WIDGET_SIZE + CHAT_GAP;
    }
    top = shownPosition.value.y + WIDGET_SIZE - height;
  } else {
    // At the right edge of the page — left of the AI panel while it is open,
    // like the header button above it.
    left = isAIDockOpen.value ? maxLeft - aiDockWidth.value : maxLeft;
    top = HEADER_HEIGHT + CHAT_GAP;
  }

  return {
    position: 'fixed' as const,
    left: `${clamp(left, VIEWPORT_MARGIN, maxLeft)}px`,
    top: `${clamp(top, VIEWPORT_MARGIN, maxTop)}px`,
    width: `${width}px`,
    height: `${height}px`,
    zIndex: '9999',
  };
});

// When expanding left, use `right` positioning so the circle stays in place
// Always explicitly reset the unused property to 'auto'
const positionStyle = computed(() => {
  if (expandsLeft.value) {
    const rightOffset = window.innerWidth - shownPosition.value.x - 48;

    return {
      right: `${rightOffset}px`,
      left: 'auto',
      top: `${shownPosition.value.y}px`,
    };
  }

  return {
    left: `${shownPosition.value.x}px`,
    right: 'auto',
    top: `${shownPosition.value.y}px`,
  };
});

function onPointerDown(event: PointerEvent) {
  if (!isFloating.value) {
    return;
  }
  popover.value?.hide();
  isDragging.value = true;
  wasDragged.value = false;
  pointerOrigin.value = { x: event.clientX, y: event.clientY };
  // A drag starts where the widget is shown, also next to the AI panel.
  dragStart.value = {
    x: event.clientX - shownPosition.value.x,
    y: event.clientY - shownPosition.value.y,
  };
  document.addEventListener('pointermove', onPointerMove);
  document.addEventListener('pointerup', onPointerUp);
}

function onPointerMove(event: PointerEvent) {
  if (!isDragging.value) {
    return;
  }
  const dx = event.clientX - pointerOrigin.value.x;
  const dy = event.clientY - pointerOrigin.value.y;
  // Require minimum movement before starting a drag
  if (!wasDragged.value) {
    if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
      return;
    }
    wasDragged.value = true;
    lockedExpandsLeft.value = expandsLeft.value;
  }
  position.value = clampPosition({
    x: event.clientX - dragStart.value.x,
    y: event.clientY - dragStart.value.y,
  });
}

function onPointerUp() {
  isDragging.value = false;
  lockedExpandsLeft.value = null;
  document.removeEventListener('pointermove', onPointerMove);
  document.removeEventListener('pointerup', onPointerUp);
  if (wasDragged.value) {
    savePosition(position.value);
  }
}

function handleClick(event: MouseEvent) {
  if (wasDragged.value) {
    event.preventDefault();
    event.stopPropagation();

    return;
  }
  // Always anchor the popover to the circle icon, not the expanded label area
  if (helpCircleEl.value) {
    popover.value.toggle({ currentTarget: helpCircleEl.value });
  } else {
    popover.value.toggle(event);
  }
}

const isAnimating = ref(false);
const justLanded = ref(false);

function flashLanded() {
  justLanded.value = true;
  setTimeout(() => {
    justLanded.value = false;
  }, 1000);
}

function animateTransition(from: DOMRect, to: DOMRect, onDone: () => void) {
  const clone = document.createElement('div');
  const size = 48;
  Object.assign(clone.style, {
    position: 'fixed',
    zIndex: '10000',
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary, #2563eb)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    left: `${from.left}px`,
    top: `${from.top}px`,
    transition: 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
    pointerEvents: 'none',
  });
  clone.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="4.93" y1="4.93" x2="9.17" y2="9.17"/><line x1="14.83" y1="14.83" x2="19.07" y2="19.07"/><line x1="14.83" y1="9.17" x2="19.07" y2="4.93"/><line x1="14.83" y1="9.17" x2="18.36" y2="5.64"/><line x1="4.93" y1="19.07" x2="9.17" y2="14.83"/></svg>';
  document.body.appendChild(clone);

  // Scale down when going to header
  const targetSize = to.width < size ? to.width / size : 1;

  requestAnimationFrame(() => {
    Object.assign(clone.style, {
      left: `${to.left + (to.width - size * targetSize) / 2}px`,
      top: `${to.top + (to.height - size * targetSize) / 2}px`,
      transform: `scale(${targetSize})`,
      opacity: '0.8',
    });
  });

  clone.addEventListener(
    'transitionend',
    () => {
      clone.remove();
      onDone();
    },
    { once: true },
  );

  // Fallback if transitionend doesn't fire
  setTimeout(() => {
    if (clone.parentNode) {
      clone.remove();
      onDone();
    }
  }, 900);
}

function toggleMode() {
  popover.value.hide();

  if (mode.value === 'float') {
    // Float → Header: animate from circle to header slot
    const circleRect = helpCircleEl.value?.getBoundingClientRect();
    const headerTarget = document.getElementById('help-widget-header');
    // The slot is empty in float mode, so use the previous sibling button to derive position
    const sibling = headerTarget?.previousElementSibling as HTMLElement | null;
    const siblingRect = sibling?.getBoundingClientRect();
    const headerRect = siblingRect
      ? ({
          left: siblingRect.left,
          top: siblingRect.top,
          width: siblingRect.width,
          height: siblingRect.height,
        } as DOMRect)
      : headerTarget?.getBoundingClientRect();

    if (circleRect && headerRect) {
      isAnimating.value = true;
      animateTransition(circleRect, headerRect, () => {
        mode.value = 'header';
        localStorage.setItem(MODE_KEY, mode.value);
        isAnimating.value = false;
        flashLanded();
      });
    } else {
      mode.value = 'header';
      localStorage.setItem(MODE_KEY, mode.value);
    }
  } else {
    // Header → Float: animate from header slot to float position
    const headerTarget = document.getElementById('help-widget-header');
    const headerRect = headerTarget?.getBoundingClientRect();
    const floatPos = loadPosition();
    const landing = besideAIDock(floatPos);
    const targetRect = {
      left: landing.x,
      top: landing.y,
      width: 48,
      height: 48,
    } as DOMRect;

    if (headerRect) {
      isAnimating.value = true;
      animateTransition(headerRect, targetRect, () => {
        mode.value = 'float';
        position.value = floatPos;
        localStorage.setItem(MODE_KEY, mode.value);
        isAnimating.value = false;
        flashLanded();
      });
    } else {
      mode.value = 'float';
      position.value = loadPosition();
      localStorage.setItem(MODE_KEY, mode.value);
    }
  }
}

async function loadSources() {
  try {
    await knowledgeSourceStore.loadSources();
  } catch (err) {
    message.error(err);
  }
}

function openCrispChat() {
  if (window.$crisp) {
    isCrispChatOpen.value = true;
    window.$crisp.push(['do', 'chat:show']);
    window.$crisp.push(['do', 'chat:open']);
    hasUnreadChat.value = false;
  }
  popover.value.hide();
}

function setupCrispListeners() {
  if (!window.$crisp) {
    return;
  }

  // When user closes the Crisp chat, hide the launcher and show our widget again
  window.$crisp.push([
    'on',
    'chat:closed',
    () => {
      window.$crisp.push(['do', 'chat:hide']);
      isCrispChatOpen.value = false;
    },
  ]);

  // Track unread messages — show badge on our widget
  window.$crisp.push([
    'on',
    'message:received',
    () => {
      // Only mark as unread if the chat box is not currently visible
      if (!isCrispChatOpen.value) {
        hasUnreadChat.value = true;
      }
    },
  ]);
}

function toAbsoluteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `https://${url}`;
}

function onResize() {
  windowHeight.value = window.innerHeight;
  windowWidth.value = window.innerWidth;
  if (isFloating.value) {
    position.value = clampPosition(position.value);
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize);
  setupCrispListeners();

  // Ensure Crisp's default launcher is hidden — our widget is the entry point
  if (window.$crisp) {
    window.$crisp.push(['do', 'chat:hide']);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  document.removeEventListener('pointermove', onPointerMove);
  document.removeEventListener('pointerup', onPointerUp);
});
</script>

<template>
  <!-- Floating mode (hidden while Crisp chat is open) -->
  <div
    v-if="isFloating"
    v-show="!isCrispChatOpen && !isAnimating"
    class="help-widget-anchor"
    :class="{ 'is-hovered': !isDragging, 'just-landed': justLanded }"
    :style="positionStyle"
    @pointerdown="onPointerDown"
    @click="handleClick"
  >
    <div
      class="help-widget-float"
      :class="{
        'is-dragging': isDragging,
        'expand-left': expandsLeft,
      }"
    >
      <div ref="helpCircleEl" class="help-circle">
        <UiIcon name="LifeBuoy" :size="28" :stroke-width="2.5" />
      </div>
      <span class="help-label">{{ $t('help.title') }}</span>
    </div>
    <div class="drag-indicator">
      <UiIcon name="GripVertical" :size="12" :stroke-width="2.5" />
    </div>
    <div v-if="hasUnreadChat" class="unread-badge" />
  </div>

  <!-- Header mode (teleported into TopHeader) -->
  <Teleport v-if="!isFloating" to="#help-widget-header" defer>
    <UiButton
      variant="flat"
      class="notification-button"
      :class="{ 'just-landed': justLanded }"
      @click="handleClick"
    >
      <div class="header-icon-wrapper">
        <UiIcon name="LifeBuoy" class="icon-size-sm" />
        <div v-if="hasUnreadChat" class="unread-badge-header" />
      </div>
    </UiButton>
  </Teleport>

  <Popover
    ref="popover"
    append-to="body"
    :pt="{ content: { style: { padding: '0' } } }"
    @show="loadSources"
  >
    <div class="help-panel" :style="helpPanelStyle">
      <div class="help-header">
        <h3>{{ $t('help.title') }}</h3>
        <button class="mode-toggle-btn" @click="toggleMode">
          <UiIcon :name="isFloating ? 'PanelTop' : 'Maximize2'" :size="16" />
        </button>
      </div>

      <div class="help-list">
        <a
          v-for="source in knowledgeSourceStore.sources"
          :key="source.id"
          :href="toAbsoluteUrl(resolveTranslation(source.url))"
          target="_blank"
          rel="noopener noreferrer"
          class="help-item"
        >
          <UiIcon name="ExternalLink" class="icon-size-sm" />
          <div class="help-item-content">
            <div class="help-item-title">
              {{ resolveTranslation(source.title) }}
            </div>
            <div v-if="source.description" class="help-item-description">
              {{ resolveTranslation(source.description) }}
            </div>
          </div>
        </a>

        <div
          v-if="knowledgeSourceStore.sources.length === 0"
          class="help-empty"
        >
          <p>{{ $t('help.empty') }}</p>
        </div>
      </div>

      <div class="help-footer">
        <UiButton
          variant="flat"
          size="medium"
          expand
          @click="
            showAiChat = true;
            popover.hide();
          "
        >
          <UiIcon name="Sparkles" class="icon-size-sm" />
          {{ $t('help.ai.buttonLabel') }}
        </UiButton>
        <UiButton variant="flat" size="medium" expand @click="openCrispChat">
          <UiIcon name="MessageCircleMore" class="icon-size-sm" />
          {{ $t('help.chatWithUs') }}
          <span v-if="hasUnreadChat" class="chat-unread-dot" />
        </UiButton>
      </div>
    </div>
  </Popover>

  <!-- ── AI Chat — teleported to body so it's never clipped by the Popover ── -->
  <Teleport to="body">
    <div v-if="showAiChat" class="help-chat-overlay" :style="chatOverlayStyle">
      <HelpAIChat @close="showAiChat = false" />
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
$widget-size: 48px;

.help-widget-anchor {
  position: fixed;
  z-index: 9;
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:hover .drag-indicator {
    opacity: 1;
  }

  &:hover .help-widget-float:not(.is-dragging) {
    max-width: 300px;
    box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.2),
      0 3px 6px rgba(0, 0, 0, 0.12);

    .help-label {
      opacity: 1;
    }
  }
}

.help-widget-float {
  height: $widget-size;
  border-radius: $widget-size * 0.5;
  background-color: var(--color-gray-200);
  color: var(--color-gray-900);
  display: flex;
  align-items: center;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.1);
  transition:
    max-width 0.25s ease,
    box-shadow 0.2s ease;
  overflow: hidden;
  max-width: $widget-size;

  // Default: expand right (circle left, label right)
  flex-direction: row;

  // Expand left: circle stays on right, label slides in from left
  &.expand-left {
    flex-direction: row-reverse;
    margin-left: auto;

    .help-label {
      padding-left: px(14);
      padding-right: var(--space-4);
    }
  }

  &.is-dragging {
    cursor: grabbing;
  }
}

.help-circle {
  width: $widget-size;
  height: $widget-size;
  min-width: $widget-size;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-primary, #2563eb);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.help-label {
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  opacity: 0;
  transition: opacity var(--duration-base) var(--ease-standard) 0.05s;
  padding-left: var(--space-4);
  padding-right: px(14);
}

.drag-indicator {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 22px;
  height: 22px;
  border-radius: var(--border-radius-circle);
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-standard);
  pointer-events: none;
  z-index: 1;
}

.unread-badge {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-error, #ef4444);
  border: 2px solid var(--color-primary, #2563eb);
  pointer-events: none;
  z-index: 1;
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

.header-icon-wrapper {
  position: relative;
  display: inline-flex;
}

.unread-badge-header {
  position: absolute;
  top: -2px;
  right: -2px;
  z-index: 1;
  width: 8px;
  height: 8px;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-error, #ef4444);
  pointer-events: none;
}

.chat-unread-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--border-radius-circle);
  background-color: var(--color-error, #ef4444);
  flex-shrink: 0;
  margin-left: var(--space-4);
}

.help-panel {
  width: 340px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Teleported chat panel — rendered directly in <body>, never clipped by Popover
.help-chat-overlay {
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-12);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  background-color: var(--color-gray-0);

  :deep(.help-ai-chat) {
    flex: 1;
    min-height: 0;
  }
}

.help-header {
  padding: var(--space-10) var(--space-12);
  border-bottom: 1px solid var(--color-gray-200);
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-size: var(--font-size-14);
    font-weight: var(--font-weight-bold);
  }
}

.mode-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--border-radius-4);
  background: transparent;
  color: var(--color-gray-400);
  cursor: pointer;
  padding: 0;

  &:hover {
    background-color: var(--color-gray-100);
    color: var(--color-gray-600);
  }
}

.help-list {
  overflow-y: auto;
  max-height: 350px;
}

.help-empty {
  padding: var(--space-24);
  text-align: center;
  color: var(--color-gray-500);
}

.help-item {
  display: flex;
  align-items: center;
  gap: var(--space-10);
  padding: var(--space-10) var(--space-12);
  text-decoration: none;
  color: inherit;
  border-bottom: 1px solid var(--color-gray-100);

  &:hover {
    background-color: var(--color-gray-50);
  }
}

.help-item-content {
  flex: 1;
  min-width: 0;
}

.help-item-title {
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.help-item-description {
  font-size: var(--font-size-11);
  color: var(--color-gray-500);
  margin-top: var(--space-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.help-item-arrow {
  color: var(--color-gray-400);
  flex-shrink: 0;
}

.help-footer {
  border-top: 1px solid var(--color-gray-200);
  padding: var(--space-8) var(--space-12);
  display: flex;
  flex-direction: row;
  gap: var(--space-8);
  justify-content: center;
}

.just-landed {
  animation: flash-landed 1s ease;
}

@keyframes flash-landed {
  0% {
    filter: brightness(1);
  }
  15% {
    filter: brightness(1.8);
    transform: scale(1.15);
  }
  30% {
    filter: brightness(1);
    transform: scale(1);
  }
  50% {
    filter: brightness(1.5);
    transform: scale(1.1);
  }
  70% {
    filter: brightness(1);
    transform: scale(1);
  }
}
</style>
